import { Dialog, makeStyles, IconButton, Button, DialogTitle, DialogActions, DialogContent, Typography } from "@material-ui/core";
import React, { useState, useEffect, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { SERVER_URL } from "../../../api";
import "../../../customStyles/image_carousel.css";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  media: {
    display: "block",
    width: "100%",
    maxHeight: "85vh",
    objectFit: "contain",
    objectPosition: "center",
    margin: "auto",
    transition: "transform 0.3s ease",
  },
  mediaZoomed: {
    transform: "scale(2)",
    cursor: "zoom-out",
  },
  dialogContent: {
    padding: "20px",
    overflow: "hidden",
    position: "relative",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  sliderContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    minHeight: 0,
    width: "100%",
    height: "100%",
    position: "relative",
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  controls: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 1000,
    display: "flex",
    gap: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: "4px",
    padding: theme.spacing(0.5),
  },
  imageContainer: {
    position: "relative",
    cursor: "zoom-in",
    overflow: "hidden",
  },
  imageInfo: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "white",
    padding: theme.spacing(1),
    textAlign: "center",
  },
  counter: {
    position: "absolute",
    top: 10,
    left: 10,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "white",
    padding: theme.spacing(0.5, 1),
    borderRadius: "4px",
    zIndex: 1000,
  },
}));

const ImageCarouselDialog = ({ open, setOpen, images, initialIndex = 0, imageTitles = [] }) => {
  const classes = useStyles();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [sliderRef, setSliderRef] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(initialIndex);

  // Normalizar images: convertir string a array si es necesario
  const normalizedImages = useMemo(() => {
    if (!images) return [];
    if (Array.isArray(images)) return images;
    if (typeof images === "string") return [images];
    return [];
  }, [images]);

  useEffect(() => {
    if (open && sliderRef && initialIndex >= 0) {
      sliderRef.slickGoTo(initialIndex);
      setCurrentSlide(initialIndex);
    }
  }, [open, sliderRef, initialIndex]);

  useEffect(() => {
    setZoomLevel(1); // Reset zoom al cambiar de imagen
  }, [currentSlide]);

  const handleClose = () => {
    setZoomLevel(1);
    setOpen(false);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
  };

  const handleDownload = () => {
    if (normalizedImages.length > 0 && currentSlide < normalizedImages.length) {
      const imageUrl = normalizedImages[currentSlide];
      const fullUrl = typeof imageUrl === "string"
        ? `${SERVER_URL}/${imageUrl}`
        : URL.createObjectURL(imageUrl);
      
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = imageUrl.split('/').pop() || 'imagen';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const settings = {
    dots: normalizedImages.length > 1,
    infinite: normalizedImages.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current, next) => setCurrentSlide(next),
    initialSlide: initialIndex,
  };

  const isVideo = (url) => {
    if (!url) return false;
    const urlString = typeof url === "object" ? url?.name : url;
    return urlString?.endsWith(".mp4") ||
           urlString?.endsWith(".webm") ||
           urlString?.endsWith(".ogg");
  };

  const getMediaSrc = (media) => {
    return typeof media === "string"
      ? `${SERVER_URL}/${media}`
      : URL.createObjectURL(media);
  };

  const currentTitle = imageTitles[currentSlide] || `Imagen ${currentSlide + 1}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      fullScreen={false}
      PaperProps={{
        style: {
          maxHeight: "95vh",
          height: "95vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle} style={{ flexShrink: 0 }}>
        <Typography variant="h6">{currentTitle}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ 
        flex: 1, 
        overflow: "hidden", 
        padding: "20px", 
        display: "flex", 
        flexDirection: "column", 
        minHeight: 0,
        position: "relative",
      }}>
        <div className={classes.sliderContainer}>
          {normalizedImages.length > 1 && (
            <div className={classes.counter}>
              {currentSlide + 1} / {normalizedImages.length}
            </div>
          )}
          <div className={classes.controls}>
            <IconButton
              onClick={handleZoomIn}
              size="small"
              style={{ color: "white" }}
              disabled={zoomLevel >= 3}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              onClick={handleZoomOut}
              size="small"
              style={{ color: "white" }}
              disabled={zoomLevel <= 1}
            >
              <ZoomOutIcon />
            </IconButton>
            <IconButton
              onClick={handleDownload}
              size="small"
              style={{ color: "white" }}
            >
              <GetAppIcon />
            </IconButton>
          </div>
          <div style={{ 
            width: "100%", 
            height: "100%", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            overflow: "hidden",
          }}>
            <Slider 
              ref={setSliderRef} 
              {...settings} 
              style={{ width: "100%", height: "100%" }}
            >
              {normalizedImages.map((media, index) => (
                <div 
                  key={index} 
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    height: "100%",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  {isVideo(media) ? (
                    <video
                      controls
                      className={classes.media}
                      src={getMediaSrc(media)}
                      style={{ 
                        transform: `scale(${zoomLevel})`, 
                        maxHeight: "85vh",
                        maxWidth: "100%",
                      }}
                    />
                  ) : (
                    <img
                      src={getMediaSrc(media)}
                      alt={`Imagen ${index + 1}`}
                      className={classes.media}
                      style={{ 
                        transform: `scale(${zoomLevel})`,
                        cursor: zoomLevel > 1 ? "zoom-out" : "zoom-in",
                        maxHeight: "85vh",
                        maxWidth: "100%",
                        height: "auto",
                      }}
                      onClick={() => {
                        if (zoomLevel === 1) {
                          handleZoomIn();
                        } else {
                          setZoomLevel(1);
                        }
                      }}
                    />
                  )}
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </DialogContent>
      <DialogActions style={{ flexShrink: 0 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={handleDownload}
          style={{ backgroundColor: "#f3691a", color: "white" }}
        >
          Descargar
        </Button>
        <Button onClick={handleClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCarouselDialog;
